class FeaturedCollections extends HTMLElement {
  constructor() {
    super();

    // Properties
    this.navButtons = [];
    this.collectionPanels = [];
    this.activeIndex = 0;

    // Bind methods
    this.handleNavButtonClick = this.handleNavButtonClick.bind(this);
    this.handleTabKeydown = this.handleTabKeydown.bind(this);
  }

  connectedCallback() {
    // Get all nav buttons
    this.navButtons = Array.from(this.querySelectorAll('.featured_collections_collection_title'));

    // Get all collection panels
    this.collectionPanels = Array.from(this.querySelectorAll('.featured_collections_collection'));

    // Attach event listeners to nav buttons
    this.navButtons.forEach((button, index) => {
      button.addEventListener('click', this.handleNavButtonClick);
      button.dataset.index = index;
    });

    // Initialize active index
    const activeButton = this.navButtons.find((button) => button.classList.contains('active'));
    this.activeIndex = activeButton ? parseInt(activeButton.dataset.index) : 0;

    // Ensure initial ARIA attributes are correct
    this.updateAriaAttributes();

    // Delegated on root: abc-personalize.js clones/replaces the tab buttons
    // to inject a "For You" tab, which would drop a per-button listener.
    this.addEventListener('keydown', this.handleTabKeydown);
  }

  disconnectedCallback() {
    // Remove event listeners
    this.navButtons.forEach((button) => {
      button.removeEventListener('click', this.handleNavButtonClick);
    });
    this.removeEventListener('keydown', this.handleTabKeydown);
  }

  handleTabKeydown(event) {
    if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;

    const currentButton = event.target.closest('.featured_collections_collection_title');
    if (!currentButton) return;

    // Query fresh — this.navButtons goes stale after abc-personalize.js injects tab buttons.
    const buttons = Array.from(this.querySelectorAll('.featured_collections_collection_title'));
    const currentIndex = buttons.indexOf(currentButton);
    if (currentIndex === -1 || buttons.length < 2) return;

    let newIndex;
    switch (event.key) {
      case 'ArrowRight':
        newIndex = (currentIndex + 1) % buttons.length;
        break;
      case 'ArrowLeft':
        newIndex = (currentIndex - 1 + buttons.length) % buttons.length;
        break;
      case 'Home':
        newIndex = 0;
        break;
      case 'End':
        newIndex = buttons.length - 1;
        break;
    }

    event.preventDefault();
    const newButton = buttons[newIndex];
    newButton.focus();
    // Reuse whichever click handler is bound (ours or abc-personalize.js's) instead of duplicating activation logic.
    newButton.click();
  }

  handleNavButtonClick(event) {
    event.preventDefault();
    const clickedButton = event.currentTarget;
    const newIndex = parseInt(clickedButton.dataset.index);

    if (newIndex === this.activeIndex) {
      return; // Already active, do nothing
    }

    // Switch active class on nav buttons
    this.navButtons[this.activeIndex].classList.remove('active');
    this.navButtons[this.activeIndex].setAttribute('aria-selected', 'false');
    this.navButtons[this.activeIndex].setAttribute('tabindex', '-1');

    clickedButton.classList.add('active');
    clickedButton.setAttribute('aria-selected', 'true');
    clickedButton.setAttribute('tabindex', '0');

    // Switch active class on collection panels
    this.collectionPanels[this.activeIndex].classList.remove('active');
    this.collectionPanels[newIndex].classList.add('active');

    // Update active index
    this.activeIndex = newIndex;

    // Update ARIA attributes
    this.updateAriaAttributes();

    // Optional: Animate transition
    // Ensure that your CSS includes the necessary styles for animation
    // If you have CSS transitions set up, they will handle the animation automatically
  }

  updateAriaAttributes() {
    // Update ARIA attributes for nav buttons
    this.navButtons.forEach((button, index) => {
      if (index === this.activeIndex) {
        button.setAttribute('aria-selected', 'true');
        button.setAttribute('tabindex', '0');
      } else {
        button.setAttribute('aria-selected', 'false');
        button.setAttribute('tabindex', '-1');
      }
    });

    // Update ARIA attributes for collection panels
    this.collectionPanels.forEach((panel, index) => {
      if (index === this.activeIndex) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });
  }
}

customElements.define('featured-collections', FeaturedCollections);
